import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient } from "../services/api";
import { User, defaultUser } from "../types/User";
import { buttonStyle, buttonBackgroundColor } from "../components/Style";
import DeleteConfirmDialog from "../components/DeleteConfirmDialog";
import RoundBox from "../components/RoundBox";
import RoundBoxInput from "../components/RoundBoxInput";

function UserCardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === "new";
  const [form, setForm] = useState<User>(defaultUser);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // 로드
  useEffect(() => {
    const load = async () => {
      if (isNew) return;
      try {
        setLoading(true);
        const data = await apiClient.getUser(Number(id));
        setForm(data);
      } catch (e: any) {
        setError(e?.message || "사용자 정보 로드 실패");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isNew]);

  const handleSave = async () => {
    if (!form.username) {
      setError("아이디를 입력해주세요.");
      return;
    }
    if (isNew && !form.password) {
      setError("비밀번호를 입력해주세요.");
      return;
    }

    try {
      setLoading(true);
      if (isNew) {
        await apiClient.createUser(form.username, form.password || "", form.role);
        navigate("/users");
      } else {
        // 비밀번호 변경
        if (form.password && form.password.trim()) {
          await apiClient.updateUserPassword(form.id!, form.password);
          setError(null);
          alert("비밀번호가 변경되었습니다.");
          setForm((f) => ({ ...f, password: "" }));
        } else {
          setError("변경할 비밀번호를 입력해주세요.");
        }
      }
    } catch (e: any) {
      setError(e?.message || "저장 실패");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    if (isNew || !form.id) return;
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      await apiClient.deleteUser(form.id!);
      navigate("/users");
    } catch (e: any) {
      setError(e?.message || "삭제 실패");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: "16px 2.5%" }}>
      <h2>사용자 카드</h2>

      {/* 폼 박스 */}
      <RoundBox label={isNew ? "사용자 추가" : "사용자 정보"} direction="vertical" style={{ maxWidth: 720 }}>
        <RoundBoxInput
          label="아이디"
          value={form.username}
          onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
          disabled={!isNew}
        />
        <RoundBoxInput
          label="비밀번호"
          type="password"
          value={form.password || ""}
          onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
          placeholder={isNew ? "비밀번호 입력" : "변경하려면 입력"}
        />

        {error && (
          <div style={{ color: "#d73a49", marginTop: 8 }}>{error}</div>
        )}

        <div style={{ display: "flex", gap: 12 }}>
          <button onClick={handleSave} disabled={loading} style={{ ...buttonStyle, ...buttonBackgroundColor.save }}>
            {isNew ? "저장" : "비밀번호 변경"}
          </button>
          {!isNew && (
            <button onClick={handleDeleteClick} disabled={loading} style={{ ...buttonStyle, ...buttonBackgroundColor.delete }}>
              삭제
            </button>
          )}
          <button onClick={() => navigate("/users")} style={{ ...buttonStyle, ...buttonBackgroundColor.cancel }}>
            목록으로
          </button>
        </div>
      </RoundBox>

      <DeleteConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        itemName={form.username}
        itemType="사용자"
      />
    </div>
  );
}

export default UserCardPage;
